import { useFormContext } from 'react-hook-form';
import Button from '@/components/UI/Button';
import type { HouseholdIncomeFormData } from '../types';

interface ReviewProps {
  onPrevious: () => void;
}

export const Review = ({ onPrevious }: ReviewProps) => {
  const { watch } = useFormContext<HouseholdIncomeFormData>();
  const formData = watch();

  const getFileCount = (files: File[]) => {
    return files?.length || 0;
  };

  const getFileNames = (files: File[]) => {
    return files?.map((file) => file.name) || [];
  };

  return (
    <div className='p-6 space-y-6 sm:rounded-r-md sm:overflow-hidden'>
      <div>
        <h3 className='text-lg leading-6 font-medium text-primaryColor-normal'>Review Your Information</h3>
        <p className='mt-2 text-sm text-gray-600'>Please review all the information and files you have provided before submitting.</p>
      </div>

      <div className='space-y-8'>
        {/* Homeowner Information */}
        <div className='bg-white p-6 rounded-lg border border-gray-200'>
          <h4 className='text-lg font-medium text-gray-900 mb-4'>Homeowner Information</h4>
          <dl className='grid grid-cols-1 gap-x-4 gap-y-4 sm:grid-cols-2'>
            <div>
              <dt className='text-sm font-medium text-gray-500'>Name</dt>
              <dd className='mt-1 text-sm text-gray-900'>{formData.homeownerInformation?.name || 'Not provided'}</dd>
            </div>
            <div>
              <dt className='text-sm font-medium text-gray-500'>Phone</dt>
              <dd className='mt-1 text-sm text-gray-900'>{formData.homeownerInformation?.phone || 'Not provided'}</dd>
            </div>
            <div>
              <dt className='text-sm font-medium text-gray-500'>Email</dt>
              <dd className='mt-1 text-sm text-gray-900'>{formData.homeownerInformation?.email || 'Not provided'}</dd>
            </div>
            <div>
              <dt className='text-sm font-medium text-gray-500'>Current Address</dt>
              <dd className='mt-1 text-sm text-gray-900'>{formData.homeownerInformation?.currentAddress || 'Not provided'}</dd>
            </div>
            <div>
              <dt className='text-sm font-medium text-gray-500'>Property Address</dt>
              <dd className='mt-1 text-sm text-gray-900'>{formData.homeownerInformation?.propertyAddress || 'Not provided'}</dd>
            </div>
            <div>
              <dt className='text-sm font-medium text-gray-500'>Purchase Price</dt>
              <dd className='mt-1 text-sm text-gray-900'>{formData.homeownerInformation?.purchasePrice || 'Not provided'}</dd>
            </div>
            <div>
              <dt className='text-sm font-medium text-gray-500'>Gender</dt>
              <dd className='mt-1 text-sm text-gray-900'>{formData.homeownerInformation?.gender || 'Not provided'}</dd>
            </div>
            <div>
              <dt className='text-sm font-medium text-gray-500'>City Employee</dt>
              <dd className='mt-1 text-sm text-gray-900'>{formData.homeownerInformation?.cityEmployee || 'Not provided'}</dd>
            </div>
            <div className='sm:col-span-2'>
              <dt className='text-sm font-medium text-gray-500'>Race</dt>
              <dd className='mt-1 text-sm text-gray-900'>
                {formData.homeownerInformation?.race?.length ? formData.homeownerInformation.race.join(', ') : 'Not provided'}
              </dd>
            </div>
          </dl>
        </div>

        {/* Employment & Tax Documents */}
        <div className='bg-white p-6 rounded-lg border border-gray-200'>
          <h4 className='text-lg font-medium text-gray-900 mb-4'>Employment & Tax Documents</h4>
          <dl className='space-y-4'>
            <div>
              <dt className='text-sm font-medium text-gray-500'>W-2 Forms</dt>
              <dd className='mt-1 text-sm text-gray-900'>
                {getFileCount(formData.employmentTax?.w2Forms)} file(s) uploaded
                {getFileNames(formData.employmentTax?.w2Forms).length > 0 && (
                  <ul className='mt-1 text-xs text-gray-600 ml-4'>
                    {getFileNames(formData.employmentTax?.w2Forms).map((name, index) => (
                      <li key={`w2-${name}-${index}`}>• {name}</li>
                    ))}
                  </ul>
                )}
              </dd>
            </div>
            <div>
              <dt className='text-sm font-medium text-gray-500'>IRS Form 1040</dt>
              <dd className='mt-1 text-sm text-gray-900'>
                {getFileCount(formData.employmentTax?.irs1040Forms)} file(s) uploaded
                {getFileNames(formData.employmentTax?.irs1040Forms).length > 0 && (
                  <ul className='mt-1 text-xs text-gray-600 ml-4'>
                    {getFileNames(formData.employmentTax?.irs1040Forms).map((name, index) => (
                      <li key={`irs1040-${name}-${index}`}>• {name}</li>
                    ))}
                  </ul>
                )}
              </dd>
            </div>
            <div>
              <dt className='text-sm font-medium text-gray-500'>Pay Stubs</dt>
              <dd className='mt-1 text-sm text-gray-900'>
                {getFileCount(formData.employmentTax?.payStubs)} file(s) uploaded
                {getFileNames(formData.employmentTax?.payStubs).length > 0 && (
                  <ul className='mt-1 text-xs text-gray-600 ml-4'>
                    {getFileNames(formData.employmentTax?.payStubs).map((name, index) => (
                      <li key={`paystub-${name}-${index}`}>• {name}</li>
                    ))}
                  </ul>
                )}
              </dd>
            </div>
          </dl>
        </div>

        {/* Cash Assets & Statements */}
        <div className='bg-white p-6 rounded-lg border border-gray-200'>
          <h4 className='text-lg font-medium text-gray-900 mb-4'>Cash Assets & Statements</h4>
          <dl className='space-y-4'>
            <div>
              <dt className='text-sm font-medium text-gray-500'>Checking/Savings Statements</dt>
              <dd className='mt-1 text-sm text-gray-900'>
                {getFileCount(formData.cashAssets?.checkingSavingsStatements)} file(s) uploaded
              </dd>
            </div>
            <div>
              <dt className='text-sm font-medium text-gray-500'>Cash App Statements</dt>
              <dd className='mt-1 text-sm text-gray-900'>{getFileCount(formData.cashAssets?.cashAppStatements)} file(s) uploaded</dd>
            </div>
            <div>
              <dt className='text-sm font-medium text-gray-500'>PayPal Statements</dt>
              <dd className='mt-1 text-sm text-gray-900'>{getFileCount(formData.cashAssets?.payPalStatements)} file(s) uploaded</dd>
            </div>
            <div>
              <dt className='text-sm font-medium text-gray-500'>Other Payment App Statements</dt>
              <dd className='mt-1 text-sm text-gray-900'>
                Venmo: {getFileCount(formData.cashAssets?.venmoStatements)}, Zelle: {getFileCount(formData.cashAssets?.zelleStatements)},
                Google Pay: {getFileCount(formData.cashAssets?.googlePayStatements)}, Revolut:{' '}
                {getFileCount(formData.cashAssets?.revolutStatements)} file(s)
              </dd>
            </div>
          </dl>
        </div>

        {/* Other Income */}
        <div className='bg-white p-6 rounded-lg border border-gray-200'>
          <h4 className='text-lg font-medium text-gray-900 mb-4'>Other Income Documents</h4>
          <dl className='space-y-4'>
            <div>
              <dt className='text-sm font-medium text-gray-500'>Pension Documents</dt>
              <dd className='mt-1 text-sm text-gray-900'>{getFileCount(formData.otherIncome?.pensionDocuments)} file(s) uploaded</dd>
            </div>
            <div>
              <dt className='text-sm font-medium text-gray-500'>Social Security Documents</dt>
              <dd className='mt-1 text-sm text-gray-900'>{getFileCount(formData.otherIncome?.socialSecurityDocuments)} file(s) uploaded</dd>
            </div>
            <div>
              <dt className='text-sm font-medium text-gray-500'>SSI Documents</dt>
              <dd className='mt-1 text-sm text-gray-900'>{getFileCount(formData.otherIncome?.ssiDocuments)} file(s) uploaded</dd>
            </div>
            <div>
              <dt className='text-sm font-medium text-gray-500'>Other Income Documents</dt>
              <dd className='mt-1 text-sm text-gray-900'>{getFileCount(formData.otherIncome?.otherIncomeDocuments)} file(s) uploaded</dd>
            </div>
          </dl>
        </div>
      </div>

      <div className='flex gap-4 justify-end mt-8'>
        <Button label='Previous' btnStyle='neutral' type='button' onClick={onPrevious} />
        <Button label='Submit Verification' btnStyle='primary' type='submit' />
      </div>
    </div>
  );
};
